import { ChevronDown, ChevronRight, User } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useMatterLocale } from '@/components/contexts/MatterJurisdictionContext';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  type ChronologyConfigChanges,
  type ChronologyVersionRow,
  type EntityMeta,
  type EventHistoryRow,
  type EventRestoreDryRunResponse,
  type RestoreDryRunResponse,
  type SourceChanges,
  type SourceMeta,
  useEventEditing,
  useEventHistory,
  useRestore,
  useVersions,
} from '@/hooks/useChronologyEditing';
import { formatDate, formatRegionalDate, type RegionalDateLocale } from '@/utils/date-utils';

// ---------------------------------------------------------------------------
// Normalized row shape: chronology version rows and per-event history rows
// share enough fields that one renderer covers both. Mode-specific actions
// (e.g. restore button) are attached via props.
// ---------------------------------------------------------------------------

interface AuditRow {
  id: string;
  version_number: number;
  trigger: string | null;
  edited_by_user_name: string | null;
  edit_payload: Record<string, unknown> | null;
  delta_payload: Record<string, unknown> | null;
  created_at: string | null;
  /** Set on chronology-level rows that touched a single event (user edits,
   *  delete/undelete, manual create). Null on rebuild rows. */
  affected_event?: { id: string; description: string | null; temporal_display: string | null } | null;
}

const FIELD_LABELS: Record<string, string> = {
  description: 'description',
  detail: 'detail',
  temporal_expression: 'date',
  tags: 'tags',
  tag_ids: 'tags',
  entities: 'entities',
  propositions: 'sources',
  doc_events: 'sources',
  relevance_score: 'background',
  period_key: 'period',
  placements: 'citations',
  add_entity_ids: 'entities',
  remove_entity_ids: 'entities',
  add_proposition_ids: 'sources',
  remove_proposition_ids: 'sources',
  add_doc_event_ids: 'sources',
  remove_doc_event_ids: 'sources',
};

const RESTORE_SOURCE_FIELDS = new Set(['propositions', 'doc_events', 'entities']);

type RowTone = 'neutral' | 'user' | 'destructive';

/** A detail line under the title.
 *  - `kind: 'diff'` shows the field's compact before → after.
 *  - `kind: 'note'` shows a single muted note (e.g. "Description updated").
 *  - `kind: 'preview'` shows a short snippet of new content (used by
 *    "create_manual" where there's no prior value to diff against).
 */
export type RowDetail =
  | { kind: 'diff'; label: string; before: string; after: string; beforeFull?: string; afterFull?: string }
  | { kind: 'note'; label: string; note: string }
  | { kind: 'preview'; label: string; text: string; textFull?: string };

interface RowSummary {
  title: string;
  tone: RowTone;
  details: RowDetail[];
}

function errorDetailMessage(err: unknown, fallback: string): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (detail && typeof detail === 'object') {
    const message = (detail as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim()) return message;
    const reason = (detail as { reason?: unknown }).reason;
    if (typeof reason === 'string' && reason.trim()) return reason;
    const error = (detail as { error?: unknown }).error;
    if (typeof error === 'string' && error.trim()) return error;
  }
  return fallback;
}

/** Render the field-level before→after view shared by `patch` and
 *  `restore_event`. Both actions emit `edited_fields` + `prev_values` +
 *  `new_values` in the same shape, so they render identically — a restore
 *  is just an edit whose source happens to be a snapshot. */
export function summarizeDiffEdit(payload: {
  edited_fields?: string[];
  patch_body?: Record<string, unknown>;
  prev_values?: Record<string, unknown>;
  new_values?: Record<string, unknown>;
} | null): RowSummary {
  const fields: string[] = payload?.edited_fields ?? [];
  const labels = Array.from(new Set(fields.map(f => FIELD_LABELS[f] ?? f.replace(/_/g, ' '))));
  const prev = payload?.prev_values || {};
  const next = payload?.new_values || {};
  const patchBody = payload?.patch_body || {};
  const details: RowDetail[] = [];
  const formatTextDiffValue = (value: string): { text: string; full?: string } => {
    if (!value) return { text: 'Empty' };
    const shortened = truncate(value, 80);
    return shortened === value ? { text: shortened } : { text: shortened, full: value };
  };
  const textDiffValue = (record: Record<string, unknown>, field: string): string | null => {
    if (!Object.hasOwn(record, field)) return null;
    const value = record[field];
    if (typeof value === 'string') return value;
    return value == null ? '' : null;
  };

  if (fields.includes('temporal_expression')) {
    const before =
      (typeof prev.temporal_display === 'string' && prev.temporal_display)
        || (typeof prev.temporal_expression === 'string' && prev.temporal_expression)
        || null;
    const after =
      (typeof next.temporal_display === 'string' && next.temporal_display)
        || (typeof next.temporal_expression === 'string' && next.temporal_expression)
        || (typeof patchBody.temporal_expression === 'string' && patchBody.temporal_expression)
        || null;
    if (before && after) details.push({ kind: 'diff', label: 'Date', before, after });
    else if (after) details.push({ kind: 'preview', label: 'Date', text: after });
    else details.push({ kind: 'note', label: 'Date', note: 'updated' });
  }

  if (fields.includes('tags')) {
    const formatTags = (value: unknown): string | null => {
      if (!Array.isArray(value)) return null;
      return value
        .map(tag => (tag as { label?: unknown; tag_key?: unknown }).label || (tag as { tag_key?: unknown }).tag_key)
        .filter((label): label is string => typeof label === 'string' && label.length > 0)
        .join(', ');
    };
    const before = formatTags(prev.tags);
    const after = formatTags(next.tags);
    if (before !== null && after !== null) {
      details.push({ kind: 'diff', label: 'Tags', before: before || 'None', after: after || 'None' });
    } else if (after) {
      details.push({ kind: 'preview', label: 'Tags', text: after });
    } else {
      details.push({ kind: 'note', label: 'Tags', note: 'updated' });
    }
  }

  if (fields.includes('description')) {
    const before = textDiffValue(prev, 'description');
    const after = textDiffValue(next, 'description') ?? textDiffValue(patchBody, 'description');
    if (before !== null && after !== null) {
      const formattedBefore = formatTextDiffValue(before);
      const formattedAfter = formatTextDiffValue(after);
      details.push({
        kind: 'diff',
        label: 'Description',
        before: formattedBefore.text,
        beforeFull: formattedBefore.full,
        after: formattedAfter.text,
        afterFull: formattedAfter.full,
      });
    } else {
      details.push({ kind: 'note', label: 'Description', note: 'updated' });
    }
  }
  if (fields.includes('detail')) {
    const before = textDiffValue(prev, 'detail');
    const after = textDiffValue(next, 'detail') ?? textDiffValue(patchBody, 'detail');
    if (before !== null && after !== null) {
      const formattedBefore = formatTextDiffValue(before);
      const formattedAfter = formatTextDiffValue(after);
      details.push({
        kind: 'diff',
        label: 'Detail',
        before: formattedBefore.text,
        beforeFull: formattedBefore.full,
        after: formattedAfter.text,
        afterFull: formattedAfter.full,
      });
    } else {
      details.push({ kind: 'note', label: 'Detail', note: 'updated' });
    }
  }
  if (fields.includes('entities')) {
    const change = next.entities as
      | { added?: { id: string; name: string }[]; removed?: { id: string; name: string }[] }
      | undefined;
    const added = change?.added ?? [];
    const removed = change?.removed ?? [];
    if (added.length) {
      details.push({
        kind: 'preview',
        label: `Added entit${added.length === 1 ? 'y' : 'ies'}`,
        text: added.map(e => e.name).join(', '),
      });
    }
    if (removed.length) {
      details.push({
        kind: 'preview',
        label: `Removed entit${removed.length === 1 ? 'y' : 'ies'}`,
        text: removed.map(e => e.name).join(', '),
      });
    }
    if (!added.length && !removed.length) {
      // Pre-resolved-meta rows (older audit entries) — keep the generic note.
      details.push({ kind: 'note', label: 'Entities', note: 'updated' });
    }
  }
  if (fields.includes('propositions') || fields.includes('doc_events')) {
    // Combine props and doc_events into one user-facing "Sources" view.
    const propChange = next.propositions as
      | { added?: SourceMeta[]; removed?: SourceMeta[] }
      | undefined;
    const docEventChange = next.doc_events as
      | { added?: SourceMeta[]; removed?: SourceMeta[] }
      | undefined;
    const addedSources = [...(propChange?.added ?? []), ...(docEventChange?.added ?? [])];
    const removedSources = [...(propChange?.removed ?? []), ...(docEventChange?.removed ?? [])];
    if (addedSources.length) {
      details.push({
        kind: 'preview',
        label: `Added source${addedSources.length === 1 ? '' : 's'}`,
        text: addedSources.map(sourceLabel).join(', '),
      });
    }
    if (removedSources.length) {
      details.push({
        kind: 'preview',
        label: `Removed source${removedSources.length === 1 ? '' : 's'}`,
        text: removedSources.map(sourceLabel).join(', '),
      });
    }
    if (!addedSources.length && !removedSources.length) {
      if (!details.some(d => d.label === 'Sources')) {
        details.push({ kind: 'note', label: 'Sources', note: 'updated' });
      }
    }
  }
  if (fields.includes('relevance_score')) {
    const mark = (value: unknown): string => (value === 0 ? 'Background' : 'Shown');
    const before = Object.hasOwn(prev, 'relevance_score') ? mark(prev.relevance_score) : null;
    const after = Object.hasOwn(next, 'relevance_score') ? mark(next.relevance_score) : null;
    if (before && after && before !== after) {
      details.push({ kind: 'diff', label: 'Background', before, after });
    } else {
      details.push({ kind: 'note', label: 'Background', note: 'updated' });
    }
  }
  if (fields.includes('period_key')) {
    const text = (value: unknown): string => (typeof value === 'string' && value ? value : 'None');
    const before = Object.hasOwn(prev, 'period_key') ? text(prev.period_key) : null;
    const after = Object.hasOwn(next, 'period_key') ? text(next.period_key) : null;
    if (before && after) details.push({ kind: 'diff', label: 'Period', before, after });
    else details.push({ kind: 'note', label: 'Period', note: 'updated' });
  }
  if (fields.includes('placements')) {
    const count = (value: unknown): number => (Array.isArray(value) ? value.length : 0);
    const before = count(prev.placements);
    const after = count(next.placements);
    if (before !== after) {
      details.push({ kind: 'diff', label: 'Citations', before: String(before), after: String(after) });
    } else {
      details.push({ kind: 'note', label: 'Citations', note: 'updated' });
    }
  }

  return {
    title: labels.length ? `Edited ${joinList(labels)}` : 'Edited event',
    tone: 'user',
    details,
  };
}

type JunctionSyncEntry = {
  removed?: number;
  restored?: number;
  updated?: number;
  source_missing?: unknown[];
};

function countJunctionSync(entry: unknown, key: 'removed' | 'restored' | 'updated'): number {
  if (!entry || typeof entry !== 'object') return 0;
  const value = (entry as JunctionSyncEntry)[key];
  return typeof value === 'number' ? value : 0;
}

function summarizeRestoreSourceFallback(payload: {
  source_changes?: SourceChanges;
  junction_sync?: Record<string, unknown>;
} | null): RowSummary | null {
  const details = summarizeSourceChanges(payload?.source_changes);
  const labels: string[] = [];
  if (
    (payload?.source_changes?.props?.added_back?.length ?? 0) > 0
    || (payload?.source_changes?.props?.removed?.length ?? 0) > 0
    || (payload?.source_changes?.doc_events?.added_back?.length ?? 0) > 0
    || (payload?.source_changes?.doc_events?.removed?.length ?? 0) > 0
  ) {
    labels.push('sources');
  }
  if (
    (payload?.source_changes?.entities?.added_back?.length ?? 0) > 0
    || (payload?.source_changes?.entities?.removed?.length ?? 0) > 0
  ) {
    labels.push('entities');
  }

  const sync = payload?.junction_sync;
  if (sync && details.length === 0) {
    const sourcesRemoved =
      countJunctionSync(sync.props, 'removed') + countJunctionSync(sync.doc_events, 'removed');
    const sourcesRestored =
      countJunctionSync(sync.props, 'restored') + countJunctionSync(sync.doc_events, 'restored');
    const sourcesUpdated =
      countJunctionSync(sync.props, 'updated') + countJunctionSync(sync.doc_events, 'updated');
    const entitiesRemoved = countJunctionSync(sync.entities, 'removed');
    const entitiesRestored = countJunctionSync(sync.entities, 'restored');
    const entitiesUpdated = countJunctionSync(sync.entities, 'updated');

    if (sourcesRemoved || sourcesRestored || sourcesUpdated) {
      labels.push('sources');
      const parts = [
        sourcesRemoved ? `removed ${sourcesRemoved}` : null,
        sourcesRestored ? `restored ${sourcesRestored}` : null,
        sourcesUpdated ? `updated ${sourcesUpdated}` : null,
      ].filter((part): part is string => Boolean(part));
      details.push({ kind: 'note', label: 'Sources', note: parts.join(', ') });
    }
    if (entitiesRemoved || entitiesRestored || entitiesUpdated) {
      labels.push('entities');
      const parts = [
        entitiesRemoved ? `removed ${entitiesRemoved}` : null,
        entitiesRestored ? `restored ${entitiesRestored}` : null,
        entitiesUpdated ? `updated ${entitiesUpdated}` : null,
      ].filter((part): part is string => Boolean(part));
      details.push({ kind: 'note', label: 'Entities', note: parts.join(', ') });
    }
  }

  if (details.length === 0) return null;
  const uniqueLabels = Array.from(new Set(labels));
  return {
    title: uniqueLabels.length
      ? `Restored ${joinList(uniqueLabels)}`
      : 'Restored event',
    tone: 'user',
    details,
  };
}

function shortText(value: unknown): { text: string; full?: string } {
  const text = typeof value === 'string' && value ? value : 'Empty';
  const shortened = truncate(text, 80);
  return shortened === text ? { text: shortened } : { text: shortened, full: text };
}

function compactValue(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value == null) return '';
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function previewList(items: string[], max = 4): string {
  if (items.length <= max) return items.join(', ');
  return `${items.slice(0, max).join(', ')} and ${items.length - max} more`;
}

type VocabularyGroup = {
  group_key: string;
  label: string;
  color?: string | null;
  sort_order?: number;
};

type VocabularyTag = {
  tag_key: string;
  label: string;
  description?: string | null;
  group_key?: string | null;
  sort_order?: number;
};

function readTagVocabulary(value: unknown): {
  groups: Map<string, VocabularyGroup>;
  tags: Map<string, VocabularyTag>;
} {
  const source = value && typeof value === 'object' ? value as {
    tag_groups?: unknown;
    tags?: unknown;
  } : {};
  const groups = new Map<string, VocabularyGroup>();
  if (Array.isArray(source.tag_groups)) {
    source.tag_groups.forEach(group => {
      if (!group || typeof group !== 'object') return;
      const raw = group as Record<string, unknown>;
      if (typeof raw.group_key !== 'string' || !raw.group_key) return;
      groups.set(raw.group_key, {
        group_key: raw.group_key,
        label: typeof raw.label === 'string' && raw.label ? raw.label : raw.group_key,
        color: typeof raw.color === 'string' ? raw.color : null,
        sort_order: typeof raw.sort_order === 'number' ? raw.sort_order : 0,
      });
    });
  }
  const tags = new Map<string, VocabularyTag>();
  if (Array.isArray(source.tags)) {
    source.tags.forEach(tag => {
      if (!tag || typeof tag !== 'object') return;
      const raw = tag as Record<string, unknown>;
      if (typeof raw.tag_key !== 'string' || !raw.tag_key) return;
      tags.set(raw.tag_key, {
        tag_key: raw.tag_key,
        label: typeof raw.label === 'string' && raw.label ? raw.label : raw.tag_key,
        description: typeof raw.description === 'string' ? raw.description : null,
        group_key: typeof raw.group_key === 'string' ? raw.group_key : null,
        sort_order: typeof raw.sort_order === 'number' ? raw.sort_order : 0,
      });
    });
  }
  return { groups, tags };
}

function tagChanged(before: VocabularyTag, after: VocabularyTag): boolean {
  return before.label !== after.label
    || (before.description || '') !== (after.description || '')
    || (before.group_key || '') !== (after.group_key || '');
}

function groupChanged(before: VocabularyGroup, after: VocabularyGroup): boolean {
  return before.label !== after.label
    || (before.color || '') !== (after.color || '');
}

function summarizeTagVocabularyDiff(beforeValue: unknown, afterValue: unknown): RowDetail[] {
  const before = readTagVocabulary(beforeValue);
  const after = readTagVocabulary(afterValue);
  const details: RowDetail[] = [];
  const addedGroups = [...after.groups.entries()]
    .filter(([key]) => !before.groups.has(key))
    .map(([, group]) => group.label);
  const removedGroups = [...before.groups.entries()]
    .filter(([key]) => !after.groups.has(key))
    .map(([, group]) => group.label);
  const changedGroups = [...after.groups.entries()]
    .flatMap(([key, group]) => {
      const prior = before.groups.get(key);
      if (!prior || !groupChanged(prior, group)) return [];
      return prior.label === group.label ? group.label : `${prior.label} -> ${group.label}`;
    });

  const addedTags = [...after.tags.entries()]
    .filter(([key]) => !before.tags.has(key))
    .map(([, tag]) => tag.label);
  const removedTags = [...before.tags.entries()]
    .filter(([key]) => !after.tags.has(key))
    .map(([, tag]) => tag.label);
  const changedTags = [...after.tags.entries()]
    .flatMap(([key, tag]) => {
      const prior = before.tags.get(key);
      if (!prior || !tagChanged(prior, tag)) return [];
      return prior.label === tag.label ? tag.label : `${prior.label} -> ${tag.label}`;
    });

  if (addedGroups.length) {
    details.push({ kind: 'preview', label: `Added group${addedGroups.length === 1 ? '' : 's'}`, text: previewList(addedGroups) });
  }
  if (removedGroups.length) {
    details.push({ kind: 'preview', label: `Removed group${removedGroups.length === 1 ? '' : 's'}`, text: previewList(removedGroups) });
  }
  if (changedGroups.length) {
    details.push({ kind: 'preview', label: `Changed group${changedGroups.length === 1 ? '' : 's'}`, text: previewList(changedGroups) });
  }
  if (addedTags.length) {
    details.push({ kind: 'preview', label: `Added tag${addedTags.length === 1 ? '' : 's'}`, text: previewList(addedTags) });
  }
  if (removedTags.length) {
    details.push({ kind: 'preview', label: `Removed tag${removedTags.length === 1 ? '' : 's'}`, text: previewList(removedTags) });
  }
  if (changedTags.length) {
    details.push({ kind: 'preview', label: `Changed tag${changedTags.length === 1 ? '' : 's'}`, text: previewList(changedTags) });
  }
  if (details.length === 0 && (beforeValue || afterValue)) {
    details.push({
      kind: 'diff',
      label: 'Tag vocabulary',
      before: `${before.tags.size} tags / ${before.groups.size} groups`,
      after: `${after.tags.size} tags / ${after.groups.size} groups`,
    });
  }
  return details;
}

function summarizeChronologyConfigChangeDetails(
  changes: Partial<ChronologyConfigChanges> | null | undefined,
): RowDetail[] {
  const fields = Array.isArray(changes?.edited_fields) ? changes.edited_fields : [];
  const prev = changes?.prev_values || {};
  const next = changes?.new_values || {};
  const details: RowDetail[] = [];
  if (fields.includes('prompt')) {
    const before = shortText(prev.prompt);
    const after = shortText(next.prompt);
    details.push({
      kind: 'diff',
      label: 'Prompt',
      before: before.text,
      beforeFull: before.full,
      after: after.text,
      afterFull: after.full,
    });
  }
  if (fields.includes('config')) {
    const before = shortText(compactValue(prev.config));
    const after = shortText(compactValue(next.config));
    details.push({
      kind: 'diff',
      label: 'Lens settings',
      before: before.text,
      beforeFull: before.full,
      after: after.text,
      afterFull: after.full,
    });
  }
  if (fields.includes('tag_vocabulary')) {
    details.push(...summarizeTagVocabularyDiff(prev.tag_vocabulary, next.tag_vocabulary));
  }
  return details;
}

function summarizeChronologyConfigEdit(payload: {
  fields?: unknown;
  edited_fields?: string[];
  prev_values?: Record<string, unknown>;
  new_values?: Record<string, unknown>;
} | null): RowSummary {
  const details = summarizeChronologyConfigChangeDetails(payload);
  const fields = new Set(
    (Array.isArray(payload?.fields) ? payload.fields : [])
      .filter((field): field is string => typeof field === 'string'),
  );
  if (details.length === 0) {
    if (fields.has('prompt')) {
      details.push({ kind: 'note', label: 'Prompt', note: 'updated' });
    }
    if (fields.has('tags') || fields.has('tag_groups')) {
      details.push({ kind: 'note', label: 'Tag vocabulary', note: 'updated' });
    }
  }

  return {
    title: 'Chronology configuration updated',
    tone: 'user',
    details,
  };
}

/** Map a row to a friendly summary. Covers every (trigger, action) the
 *  backend emits so chronology-level and event-level rows render with the
 *  same vocabulary. Tone steers visual emphasis: 'user' rows get a slightly
 *  bolder dot, 'destructive' rows tint the title.
 *
 *  ``ctx.isOldest`` lets the renderer call out the event's creation moment
 *  in event-level histories — the oldest doc_added/rebuild row is the
 *  evidence-driven creation. ``ctx.mode`` is needed because chronology-level
 *  histories also have an oldest doc_added/rebuild row, but it's the
 *  chronology's first rebuild, not an event creation. */
function summarizeRow(
  row: AuditRow,
  ctx: { mode: 'event' | 'chronology'; isOldest: boolean; locale: RegionalDateLocale },
): RowSummary {
  const payload = row.edit_payload as {
    action?: string;
    edited_fields?: string[];
    patch_body?: Record<string, unknown>;
    prev_values?: Record<string, unknown>;
    new_values?: Record<string, unknown>;
    create_body?: Record<string, unknown>;
    target_created_at?: string;
    source_changes?: SourceChanges;
    junction_sync?: Record<string, unknown>;
    fields?: unknown;
    config_changes?: ChronologyConfigChanges;
  } | null;
  const action = payload?.action;

  if (row.trigger === 'user_edit' && action === 'patch_chronology_config') {
    return summarizeChronologyConfigEdit(payload);
  }

  // patch and restore_event share the same diff-rendering shape.
  if (
    row.trigger === 'user_edit'
    && (action === 'patch' || action === 'restore_event')
  ) {
    const fields = payload?.edited_fields ?? [];
    if (action === 'restore_event' && fields.length === 0) {
      const sourceSummary = summarizeRestoreSourceFallback(payload);
      if (sourceSummary) return sourceSummary;
      return { title: 'Restored event (no changes)', tone: 'user', details: [] };
    }
    // Chronology-level rows already show the affected event below the title,
    // so the field-level diff is redundant noise — collapse to a single line.
    if (ctx.mode === 'chronology') {
      return { title: 'Edited event', tone: 'user', details: [] };
    }
    return summarizeDiffEdit(payload);
  }

  if (row.trigger === 'user_edit' && action === 'delete') {
    return { title: 'Deleted event', tone: 'destructive', details: [] };
  }
  if (row.trigger === 'user_edit' && action === 'undelete') {
    return { title: 'Restored deleted event', tone: 'user', details: [] };
  }
  if (row.trigger === 'user_edit' && action === 'revert') {
    return { title: 'Reverted manual edits', tone: 'user', details: [] };
  }
  if (row.trigger === 'user_edit' && action === 'create_manual') {
    const cb = payload?.create_body || {};
    const details: RowDetail[] = [];
    if (typeof cb.description === 'string' && cb.description) {
      const text = truncate(cb.description, 100);
      details.push({
        kind: 'preview',
        label: 'Description',
        text,
        textFull: text === cb.description ? undefined : cb.description,
      });
    }
    if (typeof cb.temporal_expression === 'string' && cb.temporal_expression) {
      details.push({ kind: 'preview', label: 'Date', text: cb.temporal_expression });
    }
    return { title: 'Created event', tone: 'user', details };
  }
  // Chronology-level restore. When this row surfaces in an event timeline
  // it's because the chronology restore touched this specific event; the
  // chronology-level title is still the truthful description of what
  // happened.
  if (row.trigger === 'user_edit' && action === 'restore') {
    const targetCreatedAt = payload?.target_created_at;
    const dateStr = targetCreatedAt
      ? formatRegionalDate(new Date(targetCreatedAt), ctx.locale, { includeTime: true })
      : null;
    const title = dateStr
      ? `Reverted chronology to version on ${dateStr}`
      : 'Reverted chronology to an earlier version';
    const delta = row.delta_payload as { created?: unknown[]; modified?: unknown[] } | null;
    const count = (delta?.created?.length ?? 0) + (delta?.modified?.length ?? 0);
    const details: RowDetail[] = count > 0
      ? [{ kind: 'note', label: `${count} event${count === 1 ? '' : 's'}`, note: 'changed' }]
      : [];
    details.push(...summarizeChronologyConfigChangeDetails(payload?.config_changes));
    return { title, tone: 'user', details };
  }
  if (row.trigger === 'user_edit') {
    const title = ctx.mode === 'chronology' ? 'Edited chronology' : 'Edited event';
    return { title, tone: 'user', details: [] };
  }
  if (row.trigger === 'doc_deleted') {
    return { title: 'Document removed', tone: 'neutral', details: [] };
  }
  if (row.trigger === 'doc_added' || row.trigger === 'rebuild') {
    // The oldest evidence-driven row in an event's own history is the
    // moment Crimson first attached the event from extracted evidence —
    // call that out as a creation. Chronology-level histories don't have
    // an analogous "creation" row, so they keep the generic title.
    const isCreation = ctx.mode === 'event' && ctx.isOldest;
    const title = isCreation
      ? 'Created event with current evidence'
      : ctx.mode === 'chronology'
        ? 'Updated chronology with latest evidence'
        : 'Updated with current evidence';
    return { title, tone: 'neutral', details: [] };
  }
  // Fallback fires when `trigger` is null — that's the initial row created
  // alongside the chronology / event itself, before any explicit edit or
  // doc-driven update.
  const fallbackTitle = ctx.mode === 'chronology' ? 'Chronology created' : 'Initial version';
  return { title: row.trigger || fallbackTitle, tone: 'neutral', details: [] };
}

/** Renders a list of `RowDetail`s in the audit timeline's gray-box style.
 *  Reused by the audit timeline and by the event-restore preview dialog so
 *  the user sees the exact same change preview pre- and post-restore. */
export function EditDetailsBox({
  details,
  className,
}: {
  details: RowDetail[];
  className?: string;
}) {
  function renderDetailText(
    text: string,
    className: string,
    fullText?: string,
  ) {
    const content = <span className={`min-w-0 break-all ${className}`}>{text}</span>;
    if (!fullText || fullText === text) return content;
    return (
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            {content}
          </TooltipTrigger>
          <TooltipContent className="max-w-xs px-2.5 py-2">
            <p className="text-[11px] leading-relaxed text-gray-700 whitespace-pre-wrap break-words">{fullText}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  if (details.length === 0) return null;
  return (
    <ul className={`min-w-0 space-y-1 rounded-md bg-gray-50/60 px-2.5 py-1.5 text-[11px] text-gray-600 ${className ?? ''}`}>
      {details.map(d => (
        <li key={detailKey(d)} className="min-w-0 flex flex-wrap items-baseline gap-x-1.5 break-words">
          <span className="shrink-0 font-medium text-gray-600">{d.label}</span>
          {d.kind === 'diff' ? (
            <>
              {renderDetailText(d.before, 'text-gray-400 line-through decoration-gray-300', d.beforeFull)}
              <span className="text-gray-300">→</span>
              {renderDetailText(d.after, 'text-gray-700', d.afterFull)}
            </>
          ) : d.kind === 'preview' ? (
            renderDetailText(d.text, 'text-gray-700', d.textFull)
          ) : (
            <span className="text-gray-400 italic">{d.note}</span>
          )}
        </li>
      ))}
    </ul>
  );
}

function detailKey(detail: RowDetail): string {
  if (detail.kind === 'diff') return `${detail.kind}:${detail.label}:${detail.before}:${detail.after}`;
  if (detail.kind === 'preview') return `${detail.kind}:${detail.label}:${detail.text}`;
  return `${detail.kind}:${detail.label}:${detail.note}`;
}

function joinList(items: string[]): string {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

function truncate(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n - 1).trimEnd()}…` : s;
}

function sourceLabel(s: SourceMeta): string {
  return s.doc_filename || (s.description ? truncate(s.description, 60) : `Source ${s.id.slice(0, 8)}`);
}

function entityLabel(e: EntityMeta): string {
  return e.name || `Entity ${e.id.slice(0, 8)}`;
}

function versionDateTimeLabel(
  version: { created_at: string | null } | null | undefined,
  locale: RegionalDateLocale,
): string {
  return version?.created_at
    ? formatRegionalDate(new Date(version.created_at), locale, { includeTime: true })
    : 'selected version';
}

// ---------------------------------------------------------------------------
// Shared timeline renderer — used by both modes.
// ---------------------------------------------------------------------------

interface AuditTimelineProps {
  rows: AuditRow[];
  isLoading: boolean;
  emptyText: string;
  /** Whose history this is. Affects how the oldest doc_added/rebuild row is
   *  titled — in event mode that row is the event's creation moment. */
  mode: 'event' | 'chronology';
  /** Optional per-row trailing action (Restore button, etc.). Receives the
   *  row plus its index from the *displayed* list (newest = 0). */
  renderRowAction?: (row: AuditRow, idx: number) => React.ReactNode;
}

interface DayGroup {
  label: string;
  rows: { row: AuditRow; idx: number }[];
}

function groupByDay(rows: AuditRow[], locale: RegionalDateLocale): DayGroup[] {
  const groups: DayGroup[] = [];
  rows.forEach((row, idx) => {
    const label = row.created_at
      ? formatRegionalDate(new Date(row.created_at), locale, { month: 'long' })
      : 'Unknown date';
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.rows.push({ row, idx });
    else groups.push({ label, rows: [{ row, idx }] });
  });
  return groups;
}

function groupKey(group: DayGroup): string {
  return `${group.label}:${group.rows[0]?.row.id ?? 'empty'}`;
}

function AuditTimeline({
  rows, isLoading, emptyText, mode, renderRowAction,
}: AuditTimelineProps) {
  const locale = useMatterLocale();
  const groups = React.useMemo(() => groupByDay(rows, locale), [rows, locale]);
  const defaultOpenGroupKeys = React.useMemo(
    () => new Set(groups.slice(0, 1).map(groupKey)),
    [groups],
  );
  const [openGroupKeys, setOpenGroupKeys] = useState<Set<string>>(defaultOpenGroupKeys);

  useEffect(() => {
    setOpenGroupKeys(defaultOpenGroupKeys);
  }, [defaultOpenGroupKeys]);

  if (isLoading) {
    return <div className="p-4 text-sm text-gray-400">Loading…</div>;
  }
  if (rows.length === 0) {
    return <div className="p-4 text-sm text-gray-400">{emptyText}</div>;
  }
  return (
    <div className="flex-1 overflow-y-auto px-5 py-4">
      {groups.map((group, gi) => (
        <section key={groupKey(group)} className={gi > 0 ? 'mt-5' : ''}>
          <button
            type="button"
            onClick={() => {
              const key = groupKey(group);
              setOpenGroupKeys(prev => {
                const next = new Set(prev);
                if (next.has(key)) next.delete(key);
                else next.add(key);
                return next;
              });
            }}
            className="mb-2 flex w-full items-center gap-1.5 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-400 transition-colors hover:text-gray-600"
            aria-expanded={openGroupKeys.has(groupKey(group))}
          >
            {openGroupKeys.has(groupKey(group)) ? (
              <ChevronDown className="h-3 w-3 shrink-0" />
            ) : (
              <ChevronRight className="h-3 w-3 shrink-0" />
            )}
            <span>{group.label}</span>
            <span className="text-gray-300">{group.rows.length}</span>
          </button>
          {openGroupKeys.has(groupKey(group)) && (
            <ol className="relative">
              {group.rows.map((entry, ri) => {
                const { row, idx } = entry;
                const isOldest = idx === rows.length - 1;
                const summary = summarizeRow(row, { mode, isOldest, locale });
                const author = row.edited_by_user_name
                  || (row.trigger === 'user_edit' ? 'Unknown user' : 'Crimson');
                const time = row.created_at
                  ? formatDate(new Date(row.created_at), 'HH:mm')
                  : null;
                const isLastInGroup = ri === group.rows.length - 1;
                const isUserAction = row.trigger === 'user_edit';
                return (
                  <li key={row.id} className="relative flex gap-2 pb-4 last:pb-0">
                    <span className="shrink-0 w-8 text-right text-[11px] text-gray-400 tabular-nums leading-5 pt-px">
                      {time ?? ''}
                    </span>
                    <div className="relative shrink-0 flex flex-col items-center w-3">
                      <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-gray-300" />
                      {!isLastInGroup && (
                        <span className="absolute top-3 -bottom-[22px] left-1/2 -translate-x-1/2 w-px bg-gray-200" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline justify-between gap-3">
                        <p className="text-sm text-gray-800">{summary.title}</p>
                        {renderRowAction?.(row, idx)}
                      </div>
                      <p className="mt-0.5 flex items-center gap-1 text-[11px] text-gray-500">
                        {isUserAction ? (
                          <User className="h-2.5 w-2.5 text-gray-400" strokeWidth={2.25} />
                        ) : (
                          <img
                            src="/logo-icon.svg"
                            alt=""
                            className="h-2.5 w-2.5 grayscale opacity-60"
                          />
                        )}
                        {author}
                      </p>
                      {row.affected_event?.description && (
                        <p className="mt-0.5 text-[11px] text-gray-500 truncate">
                          <span className="text-gray-400">on </span>
                          <span className="text-gray-700">{truncate(row.affected_event.description, 90)}</span>
                          {row.affected_event.temporal_display && (
                            <span className="ml-1.5 text-gray-400">· {row.affected_event.temporal_display}</span>
                          )}
                        </p>
                      )}
                      <EditDetailsBox details={summary.details} className="mt-1.5" />
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </section>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// AuditPanel — discriminated by `kind`. One tab handles both flows.
// ---------------------------------------------------------------------------

type AuditPanelProps =
  | {
      kind: 'chronology';
      matterGuid: string;
      chronologyId: string;
      chronologyName?: string;
      /** Triggered after a successful chronology-level restore so the parent
       *  can re-fetch chronology events. */
      onChanged?: () => void;
    }
  | {
      kind: 'event';
      matterGuid: string;
      eventGuid: string;
      /** Triggered after a successful per-event restore so the parent can
       *  re-fetch chronology events. */
      onChanged?: () => void;
    };

export function AuditPanel(props: AuditPanelProps) {
  if (props.kind === 'chronology') {
    return (
      <ChronologyAuditMode
        matterGuid={props.matterGuid}
        chronologyId={props.chronologyId}
        chronologyName={props.chronologyName}
        onChanged={props.onChanged}
      />
    );
  }
  return (
    <EventAuditMode
      matterGuid={props.matterGuid}
      eventGuid={props.eventGuid}
      onChanged={props.onChanged}
    />
  );
}

// ---------------------------------------------------------------------------
// Chronology-level mode: restore = preview-dialog driven, append-only.
// ---------------------------------------------------------------------------

function ChronologyAuditMode({
  matterGuid, chronologyId, chronologyName, onChanged,
}: {
  matterGuid: string;
  chronologyId: string;
  chronologyName?: string;
  onChanged?: () => void;
}) {
  const { versions, isLoading } = useVersions(matterGuid, chronologyId);
  const [restoreTarget, setRestoreTarget] = useState<ChronologyVersionRow | null>(null);
  const title = chronologyName ? `History for "${chronologyName}"` : 'History';

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 py-2 border-b border-gray-200">
        <h3 className="text-sm font-medium">{title}</h3>
        <p className="text-[11px] text-gray-500">All changes to this chronology</p>
      </div>

      <AuditTimeline
        rows={versions}
        isLoading={isLoading}
        emptyText="No version history yet."
        mode="chronology"
        renderRowAction={(row, idx) => idx === 0 ? null : (
          <button
            type="button"
            onClick={() => setRestoreTarget(row as ChronologyVersionRow)}
            className="shrink-0 text-[11px] text-gray-400 hover:text-gray-700"
            title="Restore the chronology to this version (creates a new version)"
          >
            Restore
          </button>
        )}
      />

      <RestorePreviewDialog
        matterGuid={matterGuid}
        chronologyId={chronologyId}
        open={!!restoreTarget}
        version={restoreTarget}
        onOpenChange={(open) => { if (!open) setRestoreTarget(null); }}
        onRestored={() => {
          setRestoreTarget(null);
          onChanged?.();
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Event-level mode: per-row inline restore via restoreEventToVersion.
// ---------------------------------------------------------------------------

function EventAuditMode({
  matterGuid, eventGuid, onChanged,
}: { matterGuid: string; eventGuid: string; onChanged?: () => void }) {
  const { history, isLoading, mutate: refetchHistory } = useEventHistory(matterGuid, eventGuid);
  const [restoreTarget, setRestoreTarget] = useState<EventHistoryRow | null>(null);

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 py-2 border-b border-gray-200">
        <h3 className="text-sm font-medium">History</h3>
        <p className="text-[11px] text-gray-500">All changes to this event</p>
      </div>

      <AuditTimeline
        rows={history as EventHistoryRow[]}
        isLoading={isLoading}
        emptyText="No changes recorded for this event yet."
        mode="event"
        renderRowAction={(row, idx) => {
          // Restore to row's own state is a no-op; hide on the latest row.
          if (idx === 0) return null;
          return (
            <button
              type="button"
              onClick={() => setRestoreTarget(row as EventHistoryRow)}
              className="shrink-0 text-[11px] text-gray-400 hover:text-gray-700"
              title="Restore this event to the state captured at this point"
            >
              Restore
            </button>
          );
        }}
      />

      <RestoreEventPreviewDialog
        matterGuid={matterGuid}
        open={!!restoreTarget}
        eventGuid={eventGuid}
        version={restoreTarget}
        onOpenChange={(open) => { if (!open) setRestoreTarget(null); }}
        onRestored={async () => {
          setRestoreTarget(null);
          await refetchHistory();
          onChanged?.();
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Restore preview dialog (chronology-level): dry-run → execute.
// ---------------------------------------------------------------------------

interface RestorePreviewDialogProps {
  matterGuid: string;
  chronologyId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  version: ChronologyVersionRow | null;
  onRestored: () => void;
}

function RestorePreviewDialog({
  matterGuid, chronologyId, open, onOpenChange, version, onRestored,
}: RestorePreviewDialogProps) {
  const locale = useMatterLocale();
  const { dryRun, execute } = useRestore(matterGuid, chronologyId);
  const [diff, setDiff] = useState<RestoreDryRunResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const versionId = version?.id ?? null;

  React.useEffect(() => {
    if (!open || !versionId) {
      setDiff(null);
      return;
    }
    let cancelled = false;
    setDiff(null);
    (async () => {
      try {
        const result = await dryRun(versionId);
        if (!cancelled) setDiff(result);
      } catch (err: unknown) {
        toast.error(errorDetailMessage(err, 'Failed to compute restore preview'));
        onOpenChange(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, versionId, dryRun, onOpenChange]);

  async function handleConfirm() {
    if (!version || noChanges) return;
    setBusy(true);
    try {
      await execute(version.id);
      toast.success(`Restored to ${versionDateTimeLabel(version, locale)}`);
      onRestored();
    } catch (err: unknown) {
      toast.error(errorDetailMessage(err, 'Restore failed'));
    } finally {
      setBusy(false);
    }
  }

  const configDetails = summarizeChronologyConfigChangeDetails(diff?.config_changes);
  const noChanges = !!diff
    && diff.events_to_bring_back_count === 0
    && diff.events_to_remove_count === 0
    && diff.events_to_update_count === 0
    && configDetails.length === 0
    && !diff.tombstones_differ;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            Restore to {versionDateTimeLabel(version, locale)}
          </DialogTitle>
        </DialogHeader>
        {diff ? (
          <div className="max-h-[70vh] space-y-3 overflow-y-auto pr-1 text-sm">
            {noChanges ? (
              <p className="text-xs text-gray-500">No changes — target equals current state.</p>
            ) : (
              <>
                <p className="text-gray-600">Restoring will:</p>
                <ul className="space-y-1 text-xs text-gray-500">
                  <li>
                    Bring back <strong className="text-emerald-700">{diff.events_to_bring_back_count}</strong>{' '}
                    event{diff.events_to_bring_back_count === 1 ? '' : 's'}
                  </li>
                  <li>
                    Delete <strong className="text-rose-700">{diff.events_to_remove_count}</strong>{' '}
                    event{diff.events_to_remove_count === 1 ? '' : 's'} we have today
                  </li>
                  <li>
                    Keep <strong className="text-slate-700">{diff.events_to_keep_count}</strong>{' '}
                    event{diff.events_to_keep_count === 1 ? '' : 's'} already present in the selected version
                  </li>
                </ul>
                {diff.events_to_update_count > 0 && (
                  <p className="text-[11px] text-gray-500">
                    {diff.events_to_update_count} kept event{diff.events_to_update_count === 1 ? '' : 's'} will also be updated to match the selected version.
                  </p>
                )}
                {configDetails.length > 0 && (
                  <EditDetailsBox details={configDetails} />
                )}
                {diff.tombstones_differ && (
                  <p className="text-[11px] text-gray-500">
                    Deletion markers from this version will be restored, so a deleted entry is not recreated on the next build.
                  </p>
                )}
              </>
            )}
          </div>
        ) : (
          <div className="text-sm text-gray-400">Computing diff…</div>
        )}
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleConfirm} disabled={busy || !diff || noChanges}>
            {busy ? 'Restoring…' : 'Restore'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Restore preview dialog (event-level): dry-run → execute. Reuses the same
// gray-box renderer as the audit timeline so the user previews the change in
// the exact shape they'll see logged after.
// ---------------------------------------------------------------------------

interface RestoreEventPreviewDialogProps {
  matterGuid: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eventGuid: string;
  version: EventHistoryRow | null;
  onRestored: () => void | Promise<void>;
}

function RestoreEventPreviewDialog({
  matterGuid, open, onOpenChange, eventGuid, version, onRestored,
}: RestoreEventPreviewDialogProps) {
  const locale = useMatterLocale();
  const { restoreEventToVersion, dryRunEventRestore } = useEventEditing(matterGuid);
  const [diff, setDiff] = useState<EventRestoreDryRunResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const versionId = version?.id ?? null;

  React.useEffect(() => {
    if (!open || !versionId) {
      setDiff(null);
      return;
    }
    let cancelled = false;
    setDiff(null);
    (async () => {
      try {
        const result = await dryRunEventRestore(eventGuid, versionId);
        if (!cancelled) setDiff(result);
      } catch (err: unknown) {
        toast.error(errorDetailMessage(err, 'Failed to compute restore preview'));
        onOpenChange(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, versionId, eventGuid, dryRunEventRestore, onOpenChange]);

  // The shared `summarizeDiffEdit` covers description/detail/date/tags/etc.
  // We extend it with: (a) deletion-state flips, (b) source-set changes
  // (sources / entities) which are applied too but aren't in
  // `edited_fields`.
  const details: RowDetail[] = React.useMemo(() => {
    if (!diff) return [];
    const base = summarizeDiffEdit({
      edited_fields: diff.edited_fields,
      prev_values: diff.prev_values,
      new_values: diff.new_values,
    }).details;
    if (diff.currently_deleted !== diff.target_deleted) {
      base.push({
        kind: 'note',
        label: 'Status',
        note: diff.target_deleted ? 'will be marked deleted' : 'will be undeleted',
      });
    }
    const sourceFieldsInDiff = diff.edited_fields.some(field => RESTORE_SOURCE_FIELDS.has(field));
    if (!sourceFieldsInDiff) {
      base.push(...summarizeSourceChanges(diff.source_changes));
    }
    return base;
  }, [diff]);

  async function handleConfirm() {
    if (!version) return;
    setBusy(true);
    try {
      await restoreEventToVersion(eventGuid, version.id);
      toast.success('Event restored');
      await onRestored();
    } catch (err: unknown) {
      toast.error(errorDetailMessage(err, 'Failed to restore event'));
    } finally {
      setBusy(false);
    }
  }

  const noChanges = diff != null && details.length === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            Restore event to {versionDateTimeLabel(version, locale)}
          </DialogTitle>
        </DialogHeader>
        {diff ? (
          <div className="max-h-[70vh] space-y-3 overflow-y-auto pr-1 text-sm">
            {noChanges ? (
              <p className="text-gray-600">No changes — this event already matches the target version.</p>
            ) : (
              <>
                <p className="text-gray-600">The following will be applied:</p>
                <EditDetailsBox details={details} />
              </>
            )}
            <p className="text-[11px] text-gray-500">
              Restores the event's text, date, tags, background mark, citations, delete state, and its linked sources and entities. Sources whose underlying records have been hard-deleted since the target version will be skipped.
            </p>
          </div>
        ) : (
          <div className="text-sm text-gray-400">Computing diff…</div>
        )}
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleConfirm} disabled={busy || !diff || noChanges}>
            {busy ? 'Restoring…' : 'Restore'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// Props (proposition-level) and doc_events (document-level) are both surfaced
// to the user as "sources" — collapse the two backend kinds into one UI list
// so the dialog never shows two separate "Sources" sections.
function summarizeSourceChanges(changes: SourceChanges | undefined): RowDetail[] {
  if (!changes) return [];
  const out: RowDetail[] = [];
  const props = changes.props;
  const docEvents = changes.doc_events;
  const entities = changes.entities;
  const sourcesAdded = [...(props?.added_back ?? []), ...(docEvents?.added_back ?? [])];
  const sourcesRemoved = [...(props?.removed ?? []), ...(docEvents?.removed ?? [])];
  const entitiesAdded = entities?.added_back ?? [];
  const entitiesRemoved = entities?.removed ?? [];
  if (sourcesAdded.length) {
    out.push({
      kind: 'preview',
      label: `Added source${sourcesAdded.length === 1 ? '' : 's'}`,
      text: sourcesAdded.map(sourceLabel).join(', '),
    });
  }
  if (sourcesRemoved.length) {
    out.push({
      kind: 'preview',
      label: `Removed source${sourcesRemoved.length === 1 ? '' : 's'}`,
      text: sourcesRemoved.map(sourceLabel).join(', '),
    });
  }
  if (entitiesAdded.length) {
    out.push({
      kind: 'preview',
      label: `Added entit${entitiesAdded.length === 1 ? 'y' : 'ies'}`,
      text: entitiesAdded.map(entityLabel).join(', '),
    });
  }
  if (entitiesRemoved.length) {
    out.push({
      kind: 'preview',
      label: `Removed entit${entitiesRemoved.length === 1 ? 'y' : 'ies'}`,
      text: entitiesRemoved.map(entityLabel).join(', '),
    });
  }
  return out;
}
